import { useState } from 'react';
import type { ProductKit, InferenceOutput, KitResponse, WeatherData } from '@nfa/types';
import { downloadKitPDF } from '../lib/pdf-export';
import { trackExport } from '../lib/analytics';

interface ExportShareProps {
  kit: ProductKit;
  inference: InferenceOutput;
  weather?: WeatherData;
  generatedAt?: string;
}

/**
 * Format a location string for display
 * Converts snake_case to Title Case (e.g., "tour_du_mont_blanc" → "Tour du Mont Blanc")
 */
function formatLocation(location: string | null): string | null {
  if (!location) return null;

  return location
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function generateKitText(kit: ProductKit, inference: InferenceOutput): string {
  const locationDisplay = formatLocation(inference.location.specific_route) ||
    formatLocation(inference.location.region) ||
    inference.location.country.charAt(0).toUpperCase() + inference.location.country.slice(1);

  const lines: string[] = [
    `NFA Kit List - ${locationDisplay}`,
    `${inference.timing.month} | ${inference.duration_days} days | ${formatLocation(inference.activity)}`,
    '',
    '---',
    '',
  ];

  // Group by category
  const grouped = new Map<string, typeof kit.items>();
  for (const item of kit.items) {
    const category = item.category_name;
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category)!.push(item);
  }

  for (const [category, items] of grouped) {
    lines.push(`## ${category.toUpperCase()}`);
    for (const item of items) {
      const weight = item.weight_g >= 1000 
        ? `${(item.weight_g / 1000).toFixed(1)}kg` 
        : `${item.weight_g}g`;
      lines.push(`- ${item.product_name} - £${item.price_gbp} (${weight})`);
    }
    lines.push('');
  }

  lines.push('---');
  lines.push(`Total: £${kit.totals.price_gbp} | ${kit.totals.weight_kg.toFixed(1)}kg | ${kit.totals.item_count} items`);
  lines.push('');
  lines.push('Generated by NFA - notfuckingabout.com');

  return lines.join('\n');
}

export function ExportShare({ kit, inference, weather, generatedAt }: ExportShareProps) {
  const [copied, setCopied] = useState<'list' | 'link' | null>(null);

  // Get the shareable URL - since kits are auto-saved, we're always on /kit/{slug}
  const getShareUrl = (): string => {
    return window.location.href;
  };

  const handleDownloadPDF = () => {
    trackExport('pdf');
    const kitResponse: KitResponse = {
      inference,
      kit,
      meta: {
        weather,
        generated_at: generatedAt || new Date().toISOString(),
      },
    };
    downloadKitPDF(kitResponse);
  };

  const handleCopyList = async () => {
    trackExport('copy');
    const text = generateKitText(kit, inference);
    try {
      await navigator.clipboard.writeText(text);
      setCopied('list');
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    trackExport('share');
    try {
      const url = getShareUrl();
      const shareLocation = formatLocation(inference.location.specific_route) ||
        formatLocation(inference.location.region) ||
        inference.location.country.charAt(0).toUpperCase() + inference.location.country.slice(1);

      if (navigator.share) {
        await navigator.share({
          title: 'My NFA Kit List',
          text: `Check out my kit for ${shareLocation}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied('link');
        setTimeout(() => setCopied(null), 2000);
      }
    } catch (err) {
      // User cancelled or error
      console.log('Share cancelled or failed:', err);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 bg-gradient-to-r from-neutral-900 to-neutral-900/50 border border-neutral-800 rounded-xl">
      <div>
        <h3 className="font-semibold text-white mb-1">Save or share your kit</h3>
        <p className="text-sm text-neutral-400">Export the list or share with your adventure buddies</p>
      </div>
      
      <div className="flex flex-wrap gap-3">
        {/* Download PDF Button */}
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 px-4 py-2.5 bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Download PDF</span>
        </button>

        {/* Copy List Button */}
        <button
          onClick={handleCopyList}
          className="flex items-center gap-2 px-4 py-2.5 bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {copied === 'list' ? (
            <>
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              <span>Copy List</span>
            </>
          )}
        </button>

        {/* Share Button */}
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-400 text-black text-sm font-semibold rounded-lg transition-colors"
        >
          {copied === 'link' ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Link Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span>Share</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
